import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService, User } from './auth.service';
import { InitResolver } from '../init.resolver';
import { NotificationService } from '../notification/notification.service';

/**
 * Oldal-jogosultság a user `menus` térképe alapján (Felhasználók → „Látható menük").
 * Ugyanez a szabály él a szerveren is: backend/src/auth/menu-access.ts.
 *
 *  - kifejezett érték (true / false) mindig dönt — adminnál is;
 *  - „Beállítások": hiányzó kulcsnál csak admin láthatja (API-kulcsok, jelszavak);
 *  - „Levelezés": a Support része — csak akkor, ha a Support is látható;
 *  - „Chat" (csapatchat): hiányzó kulcsnál csak admin — a tagokat az admin jelöli ki;
 *  - minden más: hiányzó kulcs = látható.
 */
export const MENU_SETTINGS = 'Beállítások';
export const MENU_SUPPORT = 'Support';
export const MENU_MAIL = 'Levelezés';
export const MENU_CHAT = 'Chat';
/** Foxi oldala (időzített feladatok, emlékek, költés) — kulcs nélkül csak admin. */
export const MENU_FOXI = 'Foxi';
/** Boris oldala (tudástár, a tervezőgép állapota) — kulcs nélkül csak admin. */
export const MENU_BORIS = 'Boris';
/** Farmy oldala (tudástár, a mini PC állapota) — kulcs nélkül csak admin. */
export const MENU_FARMY = 'Farmy';
/** Etsy shopok (shop felvétele, összekötése, szinkron) — kulcs nélkül csak admin. */
export const MENU_ETSY = 'Etsy shopok';
/**
 * Shop-szűrő az Etsy shopok oldalhoz: a menü-térképben `Etsy shop:<id>` = true kulcsok.
 * Ha van ilyen kulcs, a felhasználó csak ezeket a shopokat látja/kezeli; ha nincs, mindet. Admin mindig mindet.
 */
export const ETSY_SHOP_KEY_PREFIX = 'Etsy shop:';

export function allowedEtsyShopIds(menus: Record<string, boolean> | null | undefined, roles: string[] | null | undefined): number[] | null {
  if ((roles || []).includes('admin')) {
    return null;
  }
  const ids = Object.entries(menus && typeof menus === 'object' ? menus : {})
    .filter(([key, on]) => on === true && key.startsWith(ETSY_SHOP_KEY_PREFIX))
    .map(([key]) => Number(key.slice(ETSY_SHOP_KEY_PREFIX.length)))
    .filter((id) => Number.isFinite(id));
  return ids.length ? ids : null;
}

/** A szerepnevek: a belépéskor `roles: [{ role: { name } }]`, a users/me-ből `roleNames: string[]` jön. */
export function roleNamesOf(user: unknown): string[] {
  const u = user as { roleNames?: string[]; roles?: unknown[] } | null;
  if (Array.isArray(u?.roleNames)) {
    return u!.roleNames!;
  }
  return (u?.roles || [])
    .map((r) => (typeof r === 'string' ? r : (r as { role?: { name?: string } })?.role?.name || ''))
    .filter((name) => !!name);
}

export function hasMenuAccess(menus: Record<string, boolean> | null | undefined, roles: string[], menu: string): boolean {
  const map = menus && typeof menus === 'object' ? menus : {};
  if (menu === MENU_MAIL && map[MENU_SUPPORT] === false) {
    return false;
  }
  if (typeof map[menu] === 'boolean') {
    return map[menu];
  }
  if (menu === MENU_SETTINGS || menu === MENU_CHAT || menu === MENU_FOXI || menu === MENU_BORIS || menu === MENU_FARMY || menu === MENU_ETSY) {
    return roles.includes('admin');
  }
  return true;
}

export function userHasMenu(user: User | null | undefined, menu: string): boolean {
  return !!user && hasMenuAccess(user.menus, roleNamesOf(user), menu);
}

/** Az első oldal, amit a felhasználó láthat — tiltott oldalról ide irányítunk. */
const FALLBACK_ROUTES: [string, string][] = [
  ['Mai nap', '/mai-nap'], ['Rendelések', '/orders'], ['Készlet', '/inventory'], ['Komponensek', '/components'],
  ['Support', '/support'], ['Gyártás', '/gyartas'], ['Tracking', '/tracking'], ['Munkaidő', '/munkaido'],
];

/**
 * Útvonal-őr: a route `data.menu` kulcsa szerinti oldal csak annak nyílik meg, aki láthatja —
 * az URL kézi beírásával sem. Megvárja az induláskori users/me frissítést (friss menük).
 */
export const menuGuard: CanActivateFn = async (route) => {
  const menu = route.data?.['menu'] as string | undefined;
  if (!menu) {
    return true;
  }
  const auth = inject(AuthService);
  const router = inject(Router);
  const notification = inject(NotificationService);
  await inject(InitResolver).whenReady();
  // Bejelentkezés nélkül az app a login oldalra visz — azt nem itt kezeljük.
  if (!auth.user || userHasMenu(auth.user, menu)) {
    return true;
  }
  notification.show(`Ehhez az oldalhoz nincs jogosultságod (${menu}).`, 'warning');
  const target = FALLBACK_ROUTES.find(([label]) => userHasMenu(auth.user, label));
  return router.parseUrl(target ? target[1] : '/munkaido');
};
